import { Box, Link, Stack, Typography } from '@mui/material';
import { useSubmission } from 'api/submission';
import DeclinedGif from 'assets/gifs/declined.gif';
import ReferredGif from 'assets/gifs/referred.gif';
import SuccessGif from 'assets/gifs/success.gif';
import { META } from 'common/constants';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { castArray } from 'lodash-es';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { FeedbackWrapperStackStyles, ImgWrapperStyles } from './FeedbackWrapper.styles';
import type { FeedbackWrapperProps } from './FeedbackWrapper.types';

const getFeedbackImg = (feedback: string) => {
  switch (feedback) {
    case 'success':
      return <img src={SuccessGif} alt="Success" />;
    case 'declined':
      return <img src={DeclinedGif} alt="Declined" />;
    case 'referred':
      return <img src={ReferredGif} alt="Referred" />;
    default:
      return null;
  }
};

const FeedbackWrapper: FC<FeedbackWrapperProps> = ({
  children,
  title,
  subtitle,
  renderHomeButton = true,
  homeButtonText = 'Back to Home Page',
  homeButtonHref = META.HOME_LINK,
  renderAdditionalInfo = true,
  ...rest
}) => {
  const { feedback, sx } = rest;
  const { id } = useParams();
  const { data: submission } = useSubmission(id as string, false);

  return (
    <Stack gap={2} sx={[FeedbackWrapperStackStyles(feedback), ...castArray(sx)]}>
      {feedback !== 'none' && <Box sx={ImgWrapperStyles(feedback)}>{getFeedbackImg(feedback)}</Box>}

      <Stack gap={2}>
        <Typography
          variant="displayXl"
          sx={{
            color: (theme) => theme.palette.customColors.gunMetal,
          }}
        >
          {title || ''}
        </Typography>

        <Stack gap={2}>
          {subtitle && <Typography variant="body1">{subtitle || ''}</Typography>}

          {children}

          {renderAdditionalInfo && (
            <>
              {submission && (
                <Box>
                  <Typography
                    variant="body1"
                    display="inline"
                    sx={{
                      color: (theme) => theme.palette.customColors.darkGray,
                    }}
                  >
                    Quote number
                  </Typography>

                  <Typography variant="h3" display="inline" ml={0.5}>
                    {submission?.quote_number}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="body1" display="inline">
                  Call us now at our priority line
                </Typography>

                <Link
                  variant="h3"
                  sx={{ ml: 0.5, textTransform: 'none', textDecoration: 'none' }}
                  href={`tel:${META.PRIORITY_CONTACT_NUMBER}`}
                  target="_blank"
                >
                  {META.PRIORITY_CONTACT_NUMBER}
                </Link>
              </Box>

              <Box>
                <Typography variant="body1" display="inline">
                  We’re available
                </Typography>

                <Typography variant="h3" display="inline" ml={0.5}>
                  Monday-Friday 8am-9pm ET.
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </Stack>

      {renderHomeButton && (
        <PrimaryButton text={homeButtonText} href={homeButtonHref} sx={{ marginTop: 3 }} />
      )}
    </Stack>
  );
};

export default FeedbackWrapper;
